import { Component, Input, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ModalController } from '@ionic/angular';

import { Loading } from 'src/shared/components/loading/loading';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { Webhooks } from 'src/providers/webhooks/webhooks';
import { Util } from 'src/shared/util/util';
import { Integrations } from 'src/providers/integrations/integrations';
import { Workflows } from "src/providers/workflows/workflows";

import { AccountNotificationSettingsPage } from 'src/pages/more/account-notification-settings/account-notification-settings';
import { SmartLockChooserComponent } from 'src/shared/components/smart-lock-chooser/smart-lock-chooser.component';
import { RemoteLockPage } from '../../remote-lock/remote-lock';
import { AddIntegrationPage } from '../../add-integration/add-integration';
import { AutomaticBookingPage } from 'src/pages/more/automatic-booking/automatic-booking';
import { AutomaticBookingAddressPage } from 'src/pages/more/automatic-booking/automatic-booking-address/automatic-booking-address';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { WorkflowTemplatePage } from '../workflow-template/workflow-template';
import { WorkflowPage } from 'src/pages/workflows/workflow/workflow';
import { EditWorkflowPage } from 'src/pages/workflows/edit-workflow/edit-workflow';
import { WindowService } from "src/shared/providers/window.service";
import { Subscription } from 'rxjs';

@Component({
    selector: 'tidy-active-integrations',
    templateUrl: 'active-integrations.component.html',
    standalone: false
})

export class ActiveIntegrationsComponent implements OnInit {

  loaded: boolean;
  isRightSideContent: boolean;
  shownActiveIntegrations: any;
  panelClosedSubscription: Subscription;
  autoScheduleEnabledCount = 0;
  @Input() webhooks: any;
  @Input() remoteAccessAddresses: any;
  @Input() addresses: any;
  @Input() activeIntegrations: any;
  @Input() page: string;

  constructor(
    private guestReservations: GuestReservations,
    private modalCtrl: ModalController,
    private navCtrl: CustomNavController,
    private storage: TidyStorage,
    private snackBar: MatSnackBar,
    private rightSidePanelService: RightSidePanelService,
    private util: Util,
    private webhooksService: Webhooks,
    private integrations: Integrations,
    private workflowsProvider: Workflows,
    private windowService: WindowService
  ) {}

  @Loading('', true)
  async ngOnInit() {
    try {
      await this.storage.save('cameFromIntegrations', true);
      this.rightSidePanelService.setDialogLoading(true);
      if (this.page === 'active-integrations') {
        await this.getActiveIntegrations();
        this.remoteAccessAddresses = await this.integrations.getRemoteAccessAddresses();
      }
      this.markTidySources(this.activeIntegrations);
      this.shownActiveIntegrations = this.activeIntegrations;
      this.autoScheduleEnabledCount = this.addresses?.filter(
        (address) => address?.address_automatic_booking_setting?.automatic_booking_enabled
      )?.length || 0;
      this.loaded = true;
      this.rightSidePanelService.setDialogLoading(false);
    } catch (_err) {
      // Silent error handling
    }
  }

  goToRemoteLockPage() {
    const params = {
      remoteAccessAddresses: this.remoteAccessAddresses
    }
    this.goToPage('remotelock', params, RemoteLockPage);
  }

  goToConnectLocksPage() {
    this.goToPage('smart-locks/chooser', {}, SmartLockChooserComponent);
  }

  goToAccountNotificationsPage() {
    const params = {
      webhooks: this.webhooks
    }
    this.goToPage('account-notifications', params, AccountNotificationSettingsPage);
  }

  goToUpdateIntegration(integration) {
    const params = {
      reservationSrcType: {
        name: integration.guest_reservation_source_type.name,
        id: integration.guest_reservation_source_type.id,
        logo_url: integration.guest_reservation_source_type.logo_url,
      },
      sourceType: integration.guest_reservation_source_type.format,
      isUpdatingIntegration: true,
      integration,
    }
    this.goToPage('add-integration', params, AddIntegrationPage);
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
    this.snackBar.open('Copied to clipboard', 'Close', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['default-snackbar']
    })
  }

  async disableIntegration(id) {
    const params: ConfirmPageParamsModel = {
      title: 'Disable Integration?',
      body: 'This will remove any existing reservations from TIDY.',
      backText: 'Go Back',
      confirmText: 'Disable',
      confirmAction: await this.confirmDisableIntegration.bind(this, id),
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  async confirmDisableIntegration(id) {
    try {
      await this.integrations.deactivateIntegration(id);
      await this.getActiveIntegrations();
      await this.storage.save('didMakeIntegrationChanges', true);
      this.modalCtrl.dismiss();
    } catch(err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  goToAutomaticBooking() {
    const params = {
      showBackButton: true
    };
    this.goToPage('automatic-booking', params, AutomaticBookingPage);
  }

  goToAutomaticBookingAddress(integration) {
    const integrationAddressId = integration.address?.id ?? integration.addresses?.[0]?.id;
    const address = this.addresses?.find((addr) => addr.id === integrationAddressId);
    if (!address) {
      this.util.showError('Property details are still loading. Please try again.');
      return;
    }
    const params = {
      address
    };
    this.goToPage('automatic-booking-property', params, AutomaticBookingAddressPage);
  }

  async disableGuestReservationIntegration(id) {
    const params: ConfirmPageParamsModel = {
      title: 'Disable Integration?',
      body: 'This will remove any existing reservations from TIDY.',
      backText: 'Go Back',
      confirmText: 'Disable',
      confirmAction: await this.confirmDisableGuestReservationIntegration.bind(this, id),
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  async confirmDisableGuestReservationIntegration(id) {
    try {
      await this.guestReservations.deactivateIntegration(id);
      await this.getActiveIntegrations();
      await this.storage.save('didMakeIntegrationChanges', true);
      this.modalCtrl.dismiss();
    } catch(err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
      throw err;
    }
  }

  async getActiveIntegrations() {
    await this.getActiveGuestReservationSourceIntegrations();
    await this.getActiveLockIntegrations();
    await this.getActiveWebhookIntegrations();
    if (this.activeIntegrations?.length > 0) {
      await this.getIntegrationWorkflows();
    }
    this.shownActiveIntegrations = this.activeIntegrations;
  }

  async getActiveGuestReservationSourceIntegrations() {
    let activeIntegrationsResponse = await this.guestReservations.getReservationSource();
    activeIntegrationsResponse = await this.guestReservations.addIntegrationsAddressInfo(activeIntegrationsResponse, this.addresses);
    this.activeIntegrations = activeIntegrationsResponse.filter(integration => !this.showPendingOrIssueAlert(integration));
  }

  async getActiveLockIntegrations() {
    const lockIntegrations = await this.integrations.getIntegrations();
    lockIntegrations.map((integration) => {
      if (integration.type === 'remote_property_access' && integration.key === 'remote_lock') {
        this.activeIntegrations.push({
          format: 'lock',
          integration
        });
      }
      if (integration.type === 'remote_property_access' && integration.key === 'seam') {
        this.activeIntegrations.push({
          format: 'seam',
          integration
        });
      }
    });
  }

  async getActiveWebhookIntegrations() {
    this.webhooks = await this.webhooksService.getWebhooks();
    this.webhooks.map((integration) => {
      this.activeIntegrations.push({
        format: 'notification',
        integration
      });
    });
  }

  showPendingOrIssueAlert(integration) {
    if (integration?.guest_reservation_source_type?.format !== 'concierge_integration') {
      if (!integration.last_sync_at && integration?.guest_reservation_fetch_status === 'pending' && integration.addresses.length <= 0) {
        return true;
      }
      if (!integration.last_sync_at && integration?.guest_reservation_fetch_status === 'pending' && integration.addresses.length > 0) {
        return true;
      }
    }
    if (integration.state === 'paused') {
      return true;
    }
    return false;
  }

  goToIntegrationSettings(integration) {
    const params = {
      integration
    };
    this.navCtrl.navigateForward('integration-settings', params);
  }

  async startSyncReservation(id) {
    this.util.showInfo('Syncing reservation...');
    try {
      await this.integrations.startSyncReservation(id);
      this.util.showSuccess('Reservation synced successfully');
    } catch(err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async getIntegrationWorkflows() {
    try {
      const [workflows, workflowTemplates] = await Promise.all([
        this.workflowsProvider.getWorkflows(),
        this.workflowsProvider.getWorkflowTemplates()
      ]);
      this.activeIntegrations.map((integration) => {
        integration['workflows'] = workflows.filter((workflow) => workflow?.name?.toLowerCase()?.includes(integration?.guest_reservation_source_type?.name?.toLowerCase()));
        integration['workflowTemplates'] = workflowTemplates.filter((workflowTemplate) => {
          const nameMatchesIntegration = workflowTemplate?.name?.toLowerCase()?.includes(integration?.guest_reservation_source_type?.name?.toLowerCase());
          const alreadyAddedWorkflow = integration['workflows']?.some(workflow => workflow?.name === workflowTemplate?.name);
          return nameMatchesIntegration && !alreadyAddedWorkflow;
        });
      });
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  goToWorkflowTemplatePage(workflowTemplate) {
    const params = {
      workflowTemplate
    };
    this.goToPage('workflow-template', params, WorkflowTemplatePage);
  }

  goToIntegrationDocumentation(url) {
    this.util.openUrl(url);
  }

  dropDownMenuClicked(action, integration) {
    if (action === 'disableGuestReservationIntegration') {
      // The TIDY (direct-booking) source can't be deactivated — the backend
      // 403s and says to pause the listing instead.
      if (integration?.is_tidy_source) {
        this.util.showError(
          'The TIDY listing is not an integration — pause it from the Listings page instead.',
          10000
        );
        return;
      }
      this.disableGuestReservationIntegration(integration.id);
    }
    if (action === 'viewDocumentation') {
      this.goToIntegrationDocumentation(integration.guest_reservation_source_type.tidy_help_link);
    }
  }

  // The customer API exposes no slug for source types, so the tidy source is
  // identified by name 'Tidy' + format 'app' (verified against the backend
  // seed). Flagged in place so the template never calls a function.
  private markTidySources(integrations: any[]): void {
    (integrations || []).forEach((integration) => {
      const type = integration?.guest_reservation_source_type;
      integration.is_tidy_source =
        typeof type?.name === 'string' &&
        type.name.toLowerCase() === 'tidy' &&
        type?.format === 'app';
    });
  }

  getSyncedAddressesList(integration) {
    if (integration?.guest_reservation_source_type?.format === 'icalendar') {
      if (integration?.address?.address_name) {
        return integration?.address?.address_name;
      } else {
        return `${integration?.address?.address1  } ${  integration?.address?.address2 || ''}`;
      }
    } else {
      let addresses = '';
      integration.addresses.map((address) => {
        if (address?.address_name) {
          addresses += `${address?.address_name  }, `;
        } else {
          addresses += `${address?.address1  } ${  address?.address2 || ''  }, `;
        }
      });
      return addresses.slice(0, -2);
    }
  }

  pauseUnpauseWorkflow(event: any, workflow: any) {
    if (event && event.stopPropagation) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (event && event.originalEvent) {
      event.originalEvent.preventDefault();
      event.originalEvent.stopPropagation();
    }
    if (workflow.paused_at) {
      this.workflowsProvider.unpauseWorkflow(workflow.id);
    } else {
      this.workflowsProvider.pauseWorkflow(workflow.id);
    }
  }

  async goToWorkflow(event: MouseEvent, workflowId: string) {
    const toggleElement = (event.target as HTMLElement).closest('tidy-toggle');
    if (!toggleElement) {
      const workflowWithVersions = await this.workflowsProvider.getWorkflowWithVersions(workflowId);
      const params = {
        workflow: workflowWithVersions,
        cameFromActiveIntegrations: true
      }
      await this.storage.save('workflowId', workflowId);
      if (workflowWithVersions.published_version) {
        this.goToPage(`/automations/workflow/${workflowId}`, params, WorkflowPage);
      } else {
        this.goToPage(`/automations/workflow/${workflowId}/edit`, params, EditWorkflowPage);
      }
    }
  }

  async goToPage(url, params, component) {
    if (this.windowService.isDesktopRes) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        return this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
        this.panelClosedSubscription?.unsubscribe();
        this.panelClosedSubscription = this.rightSidePanelService.panelClosed().subscribe(async () => {
          await this.getActiveIntegrations();
          this.remoteAccessAddresses = await this.integrations.getRemoteAccessAddresses();
        });
      }
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

}
